import {  t  } from "@/client/libs/dummy-i18n";
import { DownloadIcon, EyeIcon, FileTextIcon, TrendUpIcon } from "@phosphor-icons/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Skeleton } from "@stackmorph/ui";

import { useUserAnalytics } from "@/client/services/analytics/analytics";
import { useResumes } from "@/client/services/resume";
import { useUser } from "@/client/services/user";

export const UsageAnalytics = () => {
  const { user } = useUser();
  const { resumes } = useResumes();
  const { analytics, loading } = useUserAnalytics();

  const resumeCount = resumes?.length ?? 0;
  const maxResumes = user?.isPremium ? Infinity : 3;
  const usagePercentage = user?.isPremium ? 0 : Math.min((resumeCount / maxResumes) * 100, 100);

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="mt-2 h-4 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({length: 4}).map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
                <Skeleton className="mt-2 h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold">{t`Usage Analytics`}</h2>
        <p className="text-sm opacity-75">{t`Track your resume activity and engagement`}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t`Resumes Created`}</CardTitle>
            <FileTextIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {resumeCount}
              {!user?.isPremium && <span className="text-sm font-normal opacity-75"> / {maxResumes}</span>}
            </div>
            {!user?.isPremium && (
              <div className="mt-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div 
                    className="h-full bg-primary transition-all duration-300" 
                    style={{ width: `${usagePercentage}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {3 - resumeCount} {t`resumes remaining`}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t`Total Views`}</CardTitle>
            <EyeIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalViews ?? 0}</div>
            <p className="text-xs text-muted-foreground">
              {user?.isPremium ? t`From your public resumes` : t`Upgrade for analytics`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t`Total Downloads`}</CardTitle>
            <DownloadIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalDownloads ?? 0}</div>
            <p className="text-xs text-muted-foreground">
              {user?.isPremium ? t`PDF downloads tracked` : t`Premium feature`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t`Top Resume`}</CardTitle>
            <TrendUpIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="truncate text-lg font-bold">
              {analytics?.topResume?.title ?? t`No data`}
            </div>
            <p className="text-xs text-muted-foreground">
              {analytics?.topResume ? t`Most viewed resume` : t`Start sharing to track`}
            </p>
          </CardContent>
        </Card>
      </div>

      {!user?.isPremium && usagePercentage >= 66 && (
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardHeader>
            <CardTitle className="text-base">{t`Approaching Limit`}</CardTitle>
            <CardDescription>
              {t`You've used ${resumeCount} of ${maxResumes} free resumes. Upgrade to Premium for unlimited resumes.`}
            </CardDescription>
          </CardHeader>
        </Card>
      )}
    </div>
  );
};
